import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { WINDOW_TOKEN } from './tokens';

export interface AppStoreLinks {
  ios: string;
  android: string;
}

@Injectable({
  providedIn: 'root',
})
export class DeviceDetectionService {
  private window = inject(WINDOW_TOKEN, { optional: true });
  private platformId = inject(PLATFORM_ID);

  private readonly APP_STORE_LINKS: AppStoreLinks = {
    ios: 'https://apps.apple.com/us/app/ledgerly-budget-expense/id6755126067',
    android:
      'https://play.google.com/store/apps/details?id=com.valerymelou.ledgerly',
  };

  /**
   * Detects if the current device is an Apple device (iOS, iPadOS, macOS)
   */
  isAppleDevice(): boolean {
    if (!isPlatformBrowser(this.platformId) || !this.window) {
      return false;
    }

    const userAgent = this.window.navigator.userAgent;

    // Check for iOS, iPadOS, iPhone, iPad, or macOS
    return (
      /iPad|iPhone|iPod/.test(userAgent) ||
      (this.window.navigator.platform === 'MacIntel' &&
        this.window.navigator.maxTouchPoints > 1) || // iPadOS
      /Macintosh|MacIntel|MacPPC|Mac68K/.test(userAgent)
    ); // macOS
  }

  /**
   * Gets the appropriate app store link based on the device
   */
  getAppStoreLink(): string {
    return this.isAppleDevice()
      ? this.APP_STORE_LINKS.ios
      : this.APP_STORE_LINKS.android;
  }

  /**
   * Gets the appropriate app store name for analytics/display
   */
  getAppStoreName(): string {
    return this.isAppleDevice() ? 'App Store' : 'Google Play';
  }

  /**
   * Gets the platform name for analytics
   */
  getPlatformName(): string {
    return this.isAppleDevice() ? 'iOS' : 'Android';
  }

  /**
   * Gets both store links (useful for components that want to show both)
   */
  getAllStoreLinks(): AppStoreLinks {
    return { ...this.APP_STORE_LINKS };
  }
}
